import { useCallback, useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { GraduationCapIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePaginatedData } from "@/hooks/usePaginatedData";
import { rowAction, type RowAction } from "@/components/portal/lms/attemptRules";
import {
  formatUtcNaive,
  lmsLearnerApi,
  type LearnerAssignment,
} from "@/services/api/lms";

export const Route = createFileRoute("/portal/learning/")({
  component: MyTestsPage,
});

const STATUS_BADGE: Record<RowAction["kind"], { label: string; variant: "default" | "secondary" | "outline" | "success" }> = {
  start: { label: "Not started", variant: "outline" },
  resume: { label: "In progress", variant: "secondary" },
  pending: { label: "Awaiting review", variant: "secondary" },
  result: { label: "Result available", variant: "default" },
  passed: { label: "Passed", variant: "success" },
};

/** Loading / error / empty placeholder, or null when the table should render. */
function listStatus(isLoading: boolean, error: string | null, count: number): ReactNode | null {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="py-12 text-center text-destructive" data-testid="lms-my-tests-error">
        {error}
      </p>
    );
  }
  if (count === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground" data-testid="lms-my-tests-empty">
        No tests assigned
      </p>
    );
  }
  return null;
}

/**
 * The learner's "My tests" — the entry point of Learning (Wave C D1). Rows
 * come from dp's owner-scoped `assigned-to-me`; the one action per row is
 * chosen by `rowAction` (D5). Start and Retake POST the assignment id and open
 * the returned attempt; Resume opens the live attempt by id (see its case).
 */
function MyTestsPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const navigate = useNavigate();
  const [startingId, setStartingId] = useState<string | null>(null);

  const fetchAssignments = useCallback(
    async (page: number, pageSize: number) => {
      if (!token || !organisationId) {
        return { data: [], totalCount: 0, page: 1, pageSize };
      }
      const response = await lmsLearnerApi.listMyAssignments(token, { page, pageSize });
      return {
        data: response.items,
        totalCount: response.total,
        page: response.page,
        pageSize: response.page_size,
      };
    },
    [token, organisationId],
  );

  const {
    data: items,
    isLoading,
    error,
    refresh,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<LearnerAssignment>({
    fetchFn: fetchAssignments,
    initialPageSize: 25,
    dependencies: [token, organisationId],
  });

  const openAttempt = (attemptId: string) =>
    navigate({ to: "/portal/learning/attempt/$attemptId", params: { attemptId } });

  // Start / Retake: one POST, then open whatever sitting dp returns.
  const start = async (assignment: LearnerAssignment) => {
    if (!token) return;
    setStartingId(assignment.id);
    try {
      const attempt = await lmsLearnerApi.startAttempt(token, assignment.id);
      await openAttempt(attempt.id);
    } catch (err) {
      // 409 (completed assignment, unpublished test) — the API's words, then
      // re-read the list so the row reflects the state that refused us.
      toast.error(err instanceof Error ? err.message : "Could not start the test");
      refresh();
    } finally {
      setStartingId(null);
    }
  };

  const actions = (assignment: LearnerAssignment, action: RowAction) => {
    const busy = startingId !== null;
    const spinner = startingId === assignment.id && <Loader2 className="h-4 w-4 animate-spin" />;
    switch (action.kind) {
      case "start":
        return (
          <Button size="sm" onClick={() => start(assignment)} disabled={busy} data-testid="lms-my-tests-action">
            {spinner || "Start"}
          </Button>
        );
      case "resume":
        // OPENS the live sitting (a GET), never POSTs: if the list is stale and
        // the sitting has since expired, a POST would have dp auto-submit it AND
        // open a new timed sitting the learner never asked for. The GET lets dp
        // auto-submit and the page render the pending view instead.
        return (
          <Button size="sm" onClick={() => openAttempt(action.attemptId)} disabled={busy} data-testid="lms-my-tests-action">
            Resume
          </Button>
        );
      case "pending":
        return (
          <Button size="sm" variant="outline" onClick={() => openAttempt(action.attemptId)} data-testid="lms-my-tests-action">
            View
          </Button>
        );
      case "result":
        return (
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => start(assignment)} disabled={busy} data-testid="lms-my-tests-retake">
              {spinner || "Retake"}
            </Button>
            <Button size="sm" onClick={() => openAttempt(action.attemptId)} data-testid="lms-my-tests-action">
              View result
            </Button>
          </div>
        );
      case "passed":
        return action.attemptId ? (
          <Button size="sm" onClick={() => openAttempt(action.attemptId as string)} data-testid="lms-my-tests-action">
            View result
          </Button>
        ) : null;
    }
  };

  return (
    <>
      <Helmet>
        <title>My tests | Portal</title>
      </Helmet>

      <div className="space-y-6" data-testid="lms-my-tests-page">
        <PageHeader
          title="My tests"
          description="Tests assigned to you. Start or resume one, and open your result once your manager has reviewed it."
          icon={GraduationCapIcon}
          eyebrow="Learning"
        />

        {listStatus(isLoading, error, items.length) ?? (
          <Table data-testid="lms-my-tests">
            <TableHeader>
              <TableRow>
                <TableHead>Test</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                <TableHead className="text-right">Attempts</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[200px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((assignment) => {
                const action = rowAction(assignment);
                const badge = STATUS_BADGE[action.kind];
                return (
                  <TableRow key={assignment.id} data-testid="lms-my-tests-row" data-action={action.kind}>
                    <TableCell>
                      <div className="font-medium">{assignment.test_title}</div>
                      {assignment.test_description && (
                        <div className="text-xs text-muted-foreground">{assignment.test_description}</div>
                      )}
                    </TableCell>
                    <TableCell>{formatUtcNaive(assignment.due_at_utc)}</TableCell>
                    <TableCell>{formatUtcNaive(assignment.assigned_at_utc)}</TableCell>
                    <TableCell className="text-right">
                      {Math.round(assignment.duration_seconds / 60)} min
                    </TableCell>
                    <TableCell className="text-right">{assignment.attempts_taken}</TableCell>
                    <TableCell>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{actions(assignment, action)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {items.length > 0 && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.pageSize}
            pageSizeOptions={pageSizeOptions}
            startIndex={startIndex}
            endIndex={endIndex}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            onPageChange={goToPage}
            onPageSizeChange={setPageSize}
            onFirstPage={firstPage}
            onLastPage={lastPage}
            onPrevPage={prevPage}
            onNextPage={nextPage}
          />
        )}
      </div>
    </>
  );
}
